import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { config } from "../../Environment";
import useRetryFetch from "../../hooks/useRetryFetch";
import { enrichVendorLogos } from "../../hooks/useProductSearch";
import LoggerContext from "../../context/LoggerProvider";
import { useDirection } from "../../hooks/useDirection";
import ProductCard from "./ProductCard";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface MarketingCategory {
  _id: string;
  slug: string;
  name: string;
  description: string;
  imageUrl?: string;
  maxProducts?: number;
}

interface MarketingSectionProps {
  onTryActivity?: (product: any) => void;
  language?: string;
}

const MarketingSections = ({ onTryActivity, language = "en" }: MarketingSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { Logger } = useContext(LoggerContext);
  const { isRTL } = useDirection();

  const [marketingCategories, setMarketingCategories] = useState<MarketingCategory[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState<Record<string, boolean>>({});
  const [canScrollLeft, setCanScrollLeft] = useState<Record<string, boolean>>({});
  const [canScrollRight, setCanScrollRight] = useState<Record<string, boolean>>({});
  const scrollRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Define checkScrollButtons before useEffect hooks that depend on it
  const checkScrollButtons = useCallback((slug: string) => {
    const container = scrollRefs.current[slug];
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    let canLeft: boolean;
    let canRight: boolean;

    if (isRTL) {
      // In RTL, scrollLeft is 0 at the start (right edge) and goes negative toward the end (left edge)
      canLeft = scrollLeft < 0;
      canRight = Math.abs(scrollLeft) < scrollWidth - clientWidth - 10;
    } else {
      canLeft = scrollLeft > 0;
      canRight = scrollLeft < scrollWidth - clientWidth - 10;
    }

    // Only update state if values actually changed
    setCanScrollLeft((prev) => {
      if (prev[slug] !== canLeft) {
        return { ...prev, [slug]: canLeft };
      }
      return prev;
    });

    setCanScrollRight((prev) => {
      if (prev[slug] !== canRight) {
        return { ...prev, [slug]: canRight };
      }
      return prev;
    });
  }, [isRTL]);

  useEffect(() => {
    loadMarketingCategories();
  }, [language]);

  // Check scroll buttons when products change
  useEffect(() => {
    Object.keys(categoryProducts).forEach((slug) => {
      if (categoryProducts[slug]?.length > 0) {
        // Delay to ensure DOM is ready
        setTimeout(() => checkScrollButtons(slug), 100);
      }
    });
  }, [categoryProducts, checkScrollButtons]);

  // Update scroll buttons on window resize
  useEffect(() => {
    const handleResize = () => {
      Object.keys(categoryProducts).forEach((slug) => {
        checkScrollButtons(slug);
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [categoryProducts, checkScrollButtons]);

  const loadMarketingCategories = async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      query.set('language', language);

      const response = await retryFetch(
        `${config.API_BASE}/api/marketing-categories?${query.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load marketing categories");
      }

      const data = await response.json();
      const categories = data.list || [];
      setMarketingCategories(categories);

      // Load products for each category
      categories.forEach((category: MarketingCategory) => {
        loadCategoryProducts(category.slug);
      });
    } catch (error) {
      Logger.error("Failed to load marketing categories", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryProducts = async (slug: string) => {
    try {
      setLoadingProducts((prev) => ({ ...prev, [slug]: true }));

      const query = new URLSearchParams();
      query.set('includeVendor', '1');

      const response = await retryFetch(
        `${config.API_BASE}/api/marketing-categories/${slug}/products?${query.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load category products");
      }

      const data = await response.json();
      const products = data.list || [];

      // Enrich with vendor logos and displayName
      const enrichedProducts = await enrichVendorLogos(products, retryFetch);

      setCategoryProducts((prev) => ({
        ...prev,
        [slug]: enrichedProducts,
      }));
    } catch (error) {
      Logger.error(`Failed to load products for category ${slug}`, error);
    } finally {
      setLoadingProducts((prev) => ({ ...prev, [slug]: false }));
    }
  };

  const scroll = useCallback((slug: string, direction: 'left' | 'right') => {
    const container = scrollRefs.current[slug];
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.8; // Scroll 80% of container width

    // In RTL, "left" (previous) means scrolling toward 0 and "right" (next) means going more negative
    const rtlMultiplier = isRTL ? -1 : 1;
    const newScrollLeft = direction === 'left'
      ? container.scrollLeft - scrollAmount * rtlMultiplier
      : container.scrollLeft + scrollAmount * rtlMultiplier;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  }, [isRTL]);

  if (loading) {
    return (
      <div className="space-y-12">
        <style>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        {[1, 2].map((i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((j) => (
                <Skeleton key={j} className="h-96 rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (marketingCategories.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      {marketingCategories.map((category) => {
        const products = categoryProducts[category.slug] || [];
        const isLoadingProducts = loadingProducts[category.slug];

        // Skip if no products loaded yet or empty
        if (!isLoadingProducts && products.length === 0) {
          return null;
        }

        return (
          <div key={category._id} className="space-y-4">
            {/* Section Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {category.imageUrl ? (
                  <img
                    src={category.imageUrl}
                    alt={category.name}
                    className="w-10 h-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{category.name}</h2>
                  {category.description && (
                    <p className="text-gray-600 text-sm mt-0.5">{category.description}</p>
                  )}
                </div>
              </div>

              {/* Carousel Navigation */}
              {!isLoadingProducts && products.length > 0 && (
                <div className="hidden md:flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => scroll(category.slug, 'left')}
                    disabled={!canScrollLeft[category.slug]}
                    className="h-9 w-9"
                  >
                    {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => scroll(category.slug, 'right')}
                    disabled={!canScrollRight[category.slug]}
                    className="h-9 w-9"
                  >
                    {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </Button>
                </div>
              )}
            </div>

            {/* Products Carousel */}
            {isLoadingProducts ? (
              <div className="flex gap-6 overflow-hidden">
                {Array(4)
                  .fill(0)
                  .map((_, index) => (
                    <div key={index} className="flex-shrink-0 w-[280px] bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                      <Skeleton className="h-32 w-full rounded-none bg-gray-200" />
                      <div className="p-3">
                        <Skeleton className="h-5 w-3/4 rounded bg-gray-200 mb-2" />
                        <Skeleton className="h-4 w-1/2 rounded bg-gray-200 mb-2" />
                        <div className="flex gap-1.5 mb-2">
                          <Skeleton className="h-5 w-16 rounded-full bg-gray-200" />
                        </div>
                        <Skeleton className="h-4 w-full rounded bg-gray-200 mb-1" />
                        <Skeleton className="h-6 w-24 rounded bg-gray-200" />
                      </div>
                      <div className="p-3 bg-gray-50 flex gap-2 mt-auto">
                        <Skeleton className="h-9 flex-1 rounded bg-gray-200" />
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="relative">
                <div
                  ref={(el) => {
                    scrollRefs.current[category.slug] = el;
                  }}
                  onScroll={() => checkScrollButtons(category.slug)}
                  className="flex items-stretch gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth pb-4"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {products.map((product) => (
                    <div key={product._id} className="flex-shrink-0 w-[280px] lg:w-[300px] snap-start h-full">
                      <ProductCard
                        product={product}
                        onTryNow={() => onTryActivity?.(product)}
                        variant="marketing"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MarketingSections;
